
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from './SpectatorPerspectiveSelector';

// MinimalCard: shows just rank and suit, for mobile/minimal mode
function MinimalCard({ card, style = {}, className = '' }) {
  if (!card) return null;
  // Unicode suit symbols
  const suitSymbols = { 'spades': '♠', 'hearts': '♥', 'diamonds': '♦', 'clubs': '♣' };
  const suit = suitSymbols[card.suit?.toLowerCase()] || card.suit || '?';
  const isRed = card.suit === 'hearts' || card.suit === 'diamonds';
  const rankFontFamily = `system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif`;
  const suitFontFamily = `'Segoe UI Symbol', 'Apple Symbols', 'Apple Color Emoji', 'Noto Color Emoji', 'Android Emoji', 'Segoe UI Emoji', 'Arial Unicode MS', Arial, sans-serif`;
  const inkColor = isRed ? '#dc2626' : '#111827';
  return (
    <div
      className={`flex flex-col justify-center items-center font-bold select-none ${className}`}
      style={{
        background: 'rgba(255,255,255,0.98)',
        border: '1.5px solid #d1d5db', // slate-300
        borderRadius: 8,
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.13)',
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        color: inkColor,
        WebkitTextFillColor: inkColor,
        ...style
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 5,
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: rankFontFamily,
          fontSize: card.rank === '10' ? 13 : 16,
          fontWeight: 700,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: card.rank === '10' ? '-0.02em' : 'normal',
          display: 'block',
          whiteSpace: 'nowrap',
          color: inkColor,
          WebkitTextFillColor: inkColor,
        }}
      >
        {String(card.rank)}
      </span>
      <span
        style={{
          position: 'absolute',
          bottom: 4,
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: suitFontFamily,
          fontSize: 21,
          lineHeight: 1,
          display: 'block',
          color: inkColor,
          WebkitTextFillColor: inkColor,
        }}
      >
        {suit}
      </span>
    </div>
  );
}

const CARD_BACK_SRC = '/cards/card_back.png';
const BASE_CARD_WIDTH = 56;
const BASE_CARD_HEIGHT = 80;
const SMALL_CARD_WIDTH = 40;
const SMALL_CARD_HEIGHT = 58;
const TABLEAU_OFFSET = 16;

function getPlayerId(player) {
  return player?.socketId || player?.userId || player?.id || player?.username || null;
}

function getCardSrc(card) {
  if (!card) return CARD_BACK_SRC;
  return `/cards/${card.code || `${card.rank}_of_${card.suit}.png`}`;
}

function getCardAlt(card) {
  if (!card) return 'Card back';
  return `${card.rank} of ${card.suit}`;
}

function SourceBadge({ selected, children }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${selected ? 'bg-amber-200 text-amber-950 dark:bg-amber-400 dark:text-slate-950' : 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200'}`}
    >
      {children}
    </span>
  );
}

function CardButton({ card, hidden = false, selected = false, highlighted = false, onClick, onDoubleClick, onDragStart, onDragEnd, draggable = false, onTouchDragStart, onTouchDragMove, onTouchDragEnd, disabled = false, className = '', style = {} }) {
  // Use minimal card on mobile screens
  const [isMinimal, setIsMinimal] = useState(() => typeof window !== 'undefined' && window.innerWidth < 860);
  const lastTouchEndRef = useRef(0);
  const touchDragState = useRef({
    dragging: false,
    started: false,
    moved: false,
    suppressClick: false,
    startX: 0,
    startY: 0,
  });

  useEffect(() => {
    function handleResize() {
      setIsMinimal(typeof window !== 'undefined' && window.innerWidth < 860);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function handleTouchStart(event) {
    if (disabled) return;
    const touch = event.touches && event.touches[0];
    touchDragState.current.dragging = true;
    touchDragState.current.started = false;
    touchDragState.current.moved = false;
    if (touch) {
      touchDragState.current.startX = touch.clientX;
      touchDragState.current.startY = touch.clientY;
    }
  }

  function handleTouchMove(event) {
    if (disabled) return;
    if (touchDragState.current.dragging) {
      const touch = event.touches && event.touches[0];
      if (touch) {
        const deltaX = touch.clientX - touchDragState.current.startX;
        const deltaY = touch.clientY - touchDragState.current.startY;
        if ((deltaX * deltaX) + (deltaY * deltaY) > 64) {
          touchDragState.current.moved = true;
          if (!touchDragState.current.started) {
            touchDragState.current.started = true;
            if (onTouchDragStart) {
              onTouchDragStart(event);
            }
          }
        }
      }
      if (touchDragState.current.started && onTouchDragMove) {
        event.preventDefault();
        onTouchDragMove(event);
      }
    }
  }

  function resetTouchDragState() {
    touchDragState.current.dragging = false;
    touchDragState.current.started = false;
    touchDragState.current.moved = false;
  }

  function handleTouchEndOrCancel(event) {
    const hadStartedDrag = touchDragState.current.started;
    if (onTouchDragEnd && touchDragState.current.started) {
      touchDragState.current.suppressClick = true;
      onTouchDragEnd(event);
      resetTouchDragState();
      return hadStartedDrag;
    }

    resetTouchDragState();
    return hadStartedDrag;
  }

  function handleTouchEnd(event) {
    if (disabled) return;
    const hadStartedDrag = handleTouchEndOrCancel(event);
    if (hadStartedDrag) return;
    if (!onDoubleClick) return;

    const now = Date.now();
    const wasDoubleTap = now - lastTouchEndRef.current < 300;
    lastTouchEndRef.current = now;

    if (wasDoubleTap) {
      event.preventDefault();
      onDoubleClick(event);
    }
  }

  function handleClick(event) {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (touchDragState.current.suppressClick) {
      touchDragState.current.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (onClick) onClick(event);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onDoubleClick={onDoubleClick}
      onTouchEnd={handleTouchEnd}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      draggable={draggable}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchCancel={handleTouchEndOrCancel}
      disabled={disabled}
      className={`relative overflow-hidden rounded-lg border shadow-sm transition ${selected ? 'border-amber-400 ring-2 ring-amber-300 dark:border-amber-300 dark:ring-amber-500/50' : highlighted ? 'border-sky-400 ring-2 ring-sky-200 dark:border-sky-300 dark:ring-sky-500/40' : 'border-slate-300 dark:border-slate-700'} ${className}`}
      style={{
        touchAction: !disabled && (draggable || onTouchDragStart) ? 'none' : 'manipulation',
        ...style,
      }}
    >
      {hidden ? (
        <img
          src={CARD_BACK_SRC}
          alt="Card back"
          className="h-full w-full object-cover"
          draggable={false}
        />
      ) : isMinimal ? (
        <MinimalCard card={card} />
      ) : (
        <img
          src={getCardSrc(card)}
          alt={getCardAlt(card)}
          className="h-full w-full object-cover"
          draggable={false}
        />
      )}
    </button>
  );
}

function EmptySlot({ label, onClick, selected = false, highlighted = false, foundation = false, disabled = false, style = {} }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center rounded-lg border-2 border-dashed text-[10px] font-semibold uppercase tracking-[0.16em] transition ${selected ? 'border-amber-400 bg-amber-50 text-amber-700 dark:border-amber-300 dark:bg-amber-400/10 dark:text-amber-200' : highlighted ? 'border-sky-400 bg-sky-50 text-sky-700 dark:border-sky-300 dark:bg-sky-500/10 dark:text-sky-200' : 'border-slate-300 bg-white/70 text-slate-400 hover:border-slate-400 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-500 dark:hover:border-slate-500'} ${foundation ? 'shadow-sm' : ''}`}
      style={style}
    >
      {label}
    </button>
  );
}

function SolitaireGame({ gameState, gamePlayers = [], user = {}, socket, roomId, isSpectator = false }) {
  // Mobile drag-and-drop helpers
  const dragTouch = useRef(null);
  function getDropTargetFromTouch(event) {
    const touch = (event.touches && event.touches[0]) || (event.changedTouches && event.changedTouches[0]);
    if (!touch) return null;
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!el) return null;
    // Look for a parent with data-drop-target attribute
    let node = el;
    while (node && node !== document.body) {
      if (node.dataset && node.dataset.dropTarget) return node.dataset.dropTarget;
      node = node.parentElement;
    }
    return null;
  }
  // Card size is always small when in minimal mode (window < 860px), never scales up until breakpoint
  const [isMinimalCardSize, setIsMinimalCardSize] = useState(() => typeof window !== 'undefined' && window.innerWidth < 860);
  useEffect(() => {
    function handleResize() {
      setIsMinimalCardSize(typeof window !== 'undefined' && window.innerWidth < 860);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const cardSize = isMinimalCardSize
    ? { width: SMALL_CARD_WIDTH, height: SMALL_CARD_HEIGHT }
    : { width: BASE_CARD_WIDTH, height: BASE_CARD_HEIGHT };
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => String(getPlayerId(player)) === String(spectatorViewPlayerId)) || null;
  }, [isSpectator, perspectiveOptions, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!perspectiveOptions.length) return;
    const existing = perspectiveOptions.some((player) => String(getPlayerId(player)) === String(spectatorViewPlayerId));
    if (!existing) {
      setSpectatorViewPlayerId(getPlayerId(perspectiveOptions[0]));
    }
  }, [isSpectator, perspectiveOptions, spectatorViewPlayerId]);

  const { submitAction, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });
  const perspectiveIdSet = useMemo(() => new Set((perspectiveIds || []).map((value) => String(value))), [perspectiveIds]);

  const allPlayers = useMemo(() => Array.isArray(gameState?.players) ? gameState.players : [], [gameState?.players]);
  const localPlayer = useMemo(() => {
    return allPlayers.find((player) => {
      return [player.socketId, player.userId, player.id, player.username]
        .filter((value) => value !== undefined && value !== null)
        .some((value) => perspectiveIdSet.has(String(value)));
    }) || null;
  }, [allPlayers, perspectiveIdSet]);

  const opponentPlayers = useMemo(() => {
    if (!localPlayer) return allPlayers;
    return allPlayers.filter((player) => String(getPlayerId(player)) !== String(getPlayerId(localPlayer)));
  }, [allPlayers, localPlayer]);

  const [selectedSource, setSelectedSource] = useState(null);
  const [highlightedTargets, setHighlightedTargets] = useState([]);
  const [touchDragPreview, setTouchDragPreview] = useState(null);
  const [isAutoCompleting, setIsAutoCompleting] = useState(false);


  useEffect(() => {
    setSelectedSource(null);
    setHighlightedTargets([]);
    setTouchDragPreview(null);
  }, [gameState?.phase, gameState?.finished]);

  const tableau = localPlayer?.tableau || [];
  const wasteTop = localPlayer?.waste?.[localPlayer.waste.length - 1] || null;
  const foundations = localPlayer?.foundations || [[], [], [], []];
  const foundationCounts = localPlayer?.foundationCounts || [0, 0, 0, 0];
  const stockCount = localPlayer?.stock?.length || 0;
  const CARD_WIDTH = cardSize.width;
  const CARD_HEIGHT = cardSize.height;
  const winnerName = useMemo(() => {
    const winnerId = gameState?.winner;
    if (!winnerId) return null;
    const winner = allPlayers.find((player) => String(getPlayerId(player)) === String(winnerId));
    return winner?.username || 'Player';
  }, [allPlayers, gameState?.winner]);
  const winnerBreakdown = useMemo(() => {
    const players = Array.isArray(gameState?.result?.breakdown?.players)
      ? gameState.result.breakdown.players
      : [];
    return players.find((player) => String(player?.socketId) === String(gameState?.winner)) || null;
  }, [gameState?.result?.breakdown?.players, gameState?.winner]);
  const boardInteractionDisabled = !!gameState?.finished;

  const selectionLabel = 'Drag cards or tap to move';
  const undoCount = Array.isArray(localPlayer?.undoStack) ? localPlayer.undoStack.length : 0;

  function isHighlightedTarget(targetKey) {
    return highlightedTargets.includes(targetKey);
  }

  function clearSelectionState() {
    setSelectedSource(null);
    setHighlightedTargets([]);
  }

  async function submitMove(action) {
    if (boardInteractionDisabled) return;
    const response = await submitAction(action);
    // Status feedback is now hidden; only clear selection on valid move
    if (response?.error) return;
    const status = response?.status;
    if (status && ['invalid_action', 'invalid_move', 'unknown_action', 'not_in_game', 'no_stock'].includes(status)) return;
    clearSelectionState();
  }

  function canPlaceOnFoundation(card, foundationPile) {
    if (!card) return false;
    if (!Array.isArray(foundationPile) || foundationPile.length === 0) return card.rank === 'A';
    const topCard = foundationPile[foundationPile.length - 1];
    return topCard.suit === card.suit && card.value === topCard.value + 1;
  }

  function canPlaceOnTableau(card, destinationPile) {
    if (!card) return false;
    if (!Array.isArray(destinationPile) || destinationPile.length === 0) return card.rank === 'K';
    const topCard = destinationPile[destinationPile.length - 1];
    return !!topCard?.faceUp && card.color !== topCard.color && card.value === topCard.value - 1;
  }

  function clonePlayerForAutoComplete(playerState) {
    return {
      tableau: Array.isArray(playerState?.tableau)
        ? playerState.tableau.map((pile) => (Array.isArray(pile) ? pile.map((card) => ({ ...card })) : []))
        : [],
      stock: Array.isArray(playerState?.stock) ? playerState.stock.map((card) => ({ ...card })) : [],
      waste: Array.isArray(playerState?.waste) ? playerState.waste.map((card) => ({ ...card })) : [],
      foundations: Array.isArray(playerState?.foundations)
        ? playerState.foundations.map((pile) => (Array.isArray(pile) ? pile.map((card) => ({ ...card })) : []))
        : [[], [], [], []],
    };
  }

  function foundationCountOf(simState) {
    return (simState.foundations || []).reduce((sum, pile) => sum + (Array.isArray(pile) ? pile.length : 0), 0);
  }

  function getNextAutoCompleteAction(simState) {
    const wasteTop = simState.waste[simState.waste.length - 1];
    if (wasteTop) {
      for (let index = 0; index < simState.foundations.length; index += 1) {
        if (canPlaceOnFoundation(wasteTop, simState.foundations[index])) {
          return { type: 'move_waste_to_foundation' };
        }
      }
    }

    for (let pileIndex = 0; pileIndex < simState.tableau.length; pileIndex += 1) {
      const pile = simState.tableau[pileIndex] || [];
      const topCard = pile[pile.length - 1];
      if (!topCard?.faceUp) continue;
      for (let foundationIndex = 0; foundationIndex < simState.foundations.length; foundationIndex += 1) {
        if (canPlaceOnFoundation(topCard, simState.foundations[foundationIndex])) {
          return { type: 'move_tableau_to_foundation', fromPile: pileIndex };
        }
      }
    }

    if (simState.stock.length > 0 || simState.waste.length > 0) {
      return { type: 'draw_stock' };
    }

    return null;
  }

  function applyAutoCompleteAction(simState, action) {
    if (!action) return false;

    if (action.type === 'move_waste_to_foundation') {
      const card = simState.waste[simState.waste.length - 1];
      if (!card) return false;
      for (let index = 0; index < simState.foundations.length; index += 1) {
        const foundation = simState.foundations[index];
        if (canPlaceOnFoundation(card, foundation)) {
          simState.waste.pop();
          foundation.push({ ...card, faceUp: true });
          return true;
        }
      }
      return false;
    }

    if (action.type === 'move_tableau_to_foundation') {
      const pile = simState.tableau[action.fromPile] || [];
      const card = pile[pile.length - 1];
      if (!card?.faceUp) return false;
      for (let index = 0; index < simState.foundations.length; index += 1) {
        const foundation = simState.foundations[index];
        if (canPlaceOnFoundation(card, foundation)) {
          pile.pop();
          if (pile.length > 0) {
            pile[pile.length - 1] = { ...pile[pile.length - 1], faceUp: true };
          }
          foundation.push({ ...card, faceUp: true });
          return true;
        }
      }
      return false;
    }

    if (action.type === 'draw_stock') {
      if (simState.stock.length > 0) {
        const nextCard = simState.stock.pop();
        simState.waste.push({ ...nextCard, faceUp: true });
        return true;
      }
      if (simState.waste.length > 0) {
        simState.stock = simState.waste.slice().reverse().map((card) => ({ ...card, faceUp: false }));
        simState.waste = [];
        return true;
      }
      return false;
    }

    return false;
  }

  function buildAutoCompletePlan(playerState) {
    if (!playerState) return null;
    const sim = clonePlayerForAutoComplete(playerState);
    const plan = [];
    const maxSteps = 520;

    for (let step = 0; step < maxSteps; step += 1) {
      if (foundationCountOf(sim) === 52) return plan;
      const action = getNextAutoCompleteAction(sim);
      if (!action) return null;
      const applied = applyAutoCompleteAction(sim, action);
      if (!applied) return null;
      plan.push(action);
    }

    return foundationCountOf(sim) === 52 ? plan : null;
  }

  const autoCompletePlan = boardInteractionDisabled || !localPlayer
    ? null
    : buildAutoCompletePlan(localPlayer);

  const canCompleteGame = !!autoCompletePlan?.length;

  function buildPossibleTargets(source) {
    if (!source || !localPlayer) return [];
    const targets = [];

    if (source.type === 'waste') {
      const card = wasteTop;
      if (!card) return [];
      foundations.forEach((foundation, foundationIndex) => {
        if (canPlaceOnFoundation(card, foundation)) {
          targets.push({
            key: `foundation-${foundationIndex}`,
            action: { type: 'move_waste_to_foundation' },
          });
        }
      });
      tableau.forEach((pile, pileIndex) => {
        if (canPlaceOnTableau(card, pile)) {
          targets.push({
            key: `tableau-${pileIndex}`,
            action: { type: 'move_waste_to_tableau', toPile: pileIndex },
          });
        }
      });
      return targets;
    }

    if (source.type === 'foundation') {
      const foundationPile = foundations[source.foundationIndex] || [];
      const card = foundationPile[foundationPile.length - 1];
      if (!card) return [];
      tableau.forEach((pile, pileIndex) => {
        if (canPlaceOnTableau(card, pile)) {
          targets.push({
            key: `tableau-${pileIndex}`,
            action: { type: 'move_foundation_to_tableau', foundationIndex: source.foundationIndex, toPile: pileIndex },
          });
        }
      });
      return targets;
    }

    const pile = tableau[source.pileIndex] || [];
    const movingCard = pile[source.startIndex];
    if (!movingCard?.faceUp) return [];

    if (source.topOnly) {
      foundations.forEach((foundation, foundationIndex) => {
        if (canPlaceOnFoundation(movingCard, foundation)) {
          targets.push({
            key: `foundation-${foundationIndex}`,
            action: { type: 'move_tableau_to_foundation', fromPile: source.pileIndex },
          });
        }
      });
    }

    tableau.forEach((destinationPile, pileIndex) => {
      if (pileIndex === source.pileIndex) return;
      if (canPlaceOnTableau(movingCard, destinationPile)) {
        targets.push({
          key: `tableau-${pileIndex}`,
          action: {
            type: 'move_tableau_sequence',
            fromPile: source.pileIndex,
            startIndex: source.startIndex,
            toPile: pileIndex,
          },
        });
      }
    });

    return targets;
  }

  function getTouchPoint(event) {
    return (event.touches && event.touches[0]) || (event.changedTouches && event.changedTouches[0]) || null;
  }

  function getDragPreviewCards(source) {
    if (!source) return [];

    if (source.type === 'waste') {
      return wasteTop ? [wasteTop] : [];
    }

    if (source.type === 'foundation') {
      const foundationPile = foundations[source.foundationIndex] || [];
      const topCard = foundationPile[foundationPile.length - 1];
      return topCard ? [topCard] : [];
    }

    if (source.type === 'tableau') {
      const pile = tableau[source.pileIndex] || [];
      return pile.slice(source.startIndex).filter((card) => card?.faceUp);
    }

    return [];
  }

  function buildTouchDragPreview(event, source) {
    const touch = getTouchPoint(event);
    const cards = getDragPreviewCards(source);
    if (!touch || !cards.length) return null;

    return {
      x: touch.clientX,
      y: touch.clientY,
      cards,
    };
  }

  function handleFoundationDrop(source = selectedSource) {
    if (boardInteractionDisabled) return;
    if (!source) return;
    if (source.type === 'waste') {
      submitMove({ type: 'move_waste_to_foundation' });
      return;
    }
    if (source.type === 'tableau' && source.topOnly) {
      submitMove({ type: 'move_tableau_to_foundation', fromPile: source.pileIndex });
      return;
    }
    // Only the top waste or tableau card can move to a foundation (no status shown)
  }

  function handleAutoMove(source) {
    if (boardInteractionDisabled) return;
    const targets = buildPossibleTargets(source);
    const foundationTarget = targets.find((target) => target.key.startsWith('foundation-'));
    if (foundationTarget) {
      submitMove(foundationTarget.action);
      return;
    }
    if (targets.length > 0) {
      submitMove(targets[0].action);
      return;
    }
    clearSelectionState();
  }

  function handleTouchDragStart(event, source) {
    if (boardInteractionDisabled) return;
    const targets = buildPossibleTargets(source);
    setSelectedSource(source);
    setHighlightedTargets(targets.map((target) => target.key));
    dragTouch.current = source;
    setTouchDragPreview(buildTouchDragPreview(event, source));
  }

  function handleTouchDragMove(event) {
    if (boardInteractionDisabled) return;
    const touch = getTouchPoint(event);
    if (!touch) return;
    setTouchDragPreview((currentPreview) => {
      if (!currentPreview) return currentPreview;
      return {
        ...currentPreview,
        x: touch.clientX,
        y: touch.clientY,
      };
    });
  }

  function handleTouchDragEnd(event) {
    if (boardInteractionDisabled) return;
    if (!dragTouch.current) return;

    const source = dragTouch.current;

    const dropKey = getDropTargetFromTouch(event);
    if (dropKey && dropKey.startsWith('tableau-')) {
      const pileIndex = parseInt(dropKey.replace('tableau-', ''), 10);
      handleTableauDrop(pileIndex, source);
    } else if (dropKey && dropKey.startsWith('foundation-')) {
      handleFoundationDrop(source);
    }

    dragTouch.current = null;
    setTouchDragPreview(null);
    clearSelectionState();
  }

  function handleDragStart(event, source) {
    if (boardInteractionDisabled) return;
    const targets = buildPossibleTargets(source);
    setSelectedSource(source);
    setHighlightedTargets(targets.map((target) => target.key));

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', JSON.stringify(source));
    }
  }

  function handleDragEnd() {
    if (boardInteractionDisabled) return;
    dragTouch.current = null;
    setTouchDragPreview(null);
    clearSelectionState();
  }

  function handleFoundationDragOver(event, foundationIndex) {
    if (boardInteractionDisabled) return;
    if (!selectedSource) return;
    if (!isHighlightedTarget(`foundation-${foundationIndex}`)) return;
    event.preventDefault();
  }

  function handleFoundationDropEvent(event, foundationIndex) {
    if (boardInteractionDisabled) return;
    if (!selectedSource) return;
    if (!isHighlightedTarget(`foundation-${foundationIndex}`)) return;
    event.preventDefault();
    handleFoundationDrop();
  }

  function handleTableauDragOver(event, pileIndex) {
    if (boardInteractionDisabled) return;
    if (!selectedSource) return;
    if (!isHighlightedTarget(`tableau-${pileIndex}`)) return;
    event.preventDefault();
  }

  function handleTableauDropEvent(event, pileIndex) {
    if (boardInteractionDisabled) return;
    if (!selectedSource) return;
    if (!isHighlightedTarget(`tableau-${pileIndex}`)) return;
    event.preventDefault();
    handleTableauDrop(pileIndex);
  }

  function handleTableauDrop(targetPileIndex, source = selectedSource) {
    if (boardInteractionDisabled) return;
    if (!source) return;
    if (source.type === 'waste') {
      submitMove({ type: 'move_waste_to_tableau', toPile: targetPileIndex });
      return;
    }
    if (source.type === 'foundation') {
      submitMove({
        type: 'move_foundation_to_tableau',
        foundationIndex: source.foundationIndex,
        toPile: targetPileIndex,
      });
      return;
    }
    submitMove({
      type: 'move_tableau_sequence',
      fromPile: source.pileIndex,
      startIndex: source.startIndex,
      toPile: targetPileIndex,
    });
  }

  async function handleCompleteGame() {
    if (boardInteractionDisabled || isAutoCompleting || !canCompleteGame) return;

    setIsAutoCompleting(true);
    clearSelectionState();

    try {
      const steps = autoCompletePlan || [];
      for (const action of steps) {
        if (gameState?.finished) break;
        const response = await submitAction(action);
        if (response?.error) break;
        const status = response?.status;
        if (status && ['invalid_action', 'invalid_move', 'unknown_action', 'not_in_game', 'no_stock'].includes(status)) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 25));
      }
    } finally {
      setIsAutoCompleting(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-cyan-50 p-3 text-slate-900 shadow-lg ring-1 ring-slate-200 dark:from-slate-950 dark:via-slate-900 dark:to-emerald-950 dark:text-slate-100 dark:ring-slate-800 sm:p-4">
      {isSpectator && perspectiveOptions.length > 0 ? (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ''}
          onChange={setSpectatorViewPlayerId}
        />
      ) : null}


      {/* Game board and controls */}

      {gameState?.finished ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-100">
          {winnerName
            ? `${winnerName} finished the deal first${winnerBreakdown?.completionTimeDisplay ? ` in ${winnerBreakdown.completionTimeDisplay}` : ''}.`
            : 'The deal is complete.'}
        </div>
      ) : null}



      <div className="rounded-3xl bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.18),_transparent_40%),radial-gradient(circle_at_bottom_right,_rgba(6,182,212,0.14),_transparent_35%)] p-3 ring-1 ring-emerald-200/70 dark:ring-emerald-900/50">
        <div className="flex flex-wrap items-start justify-between gap-3 overflow-x-auto pb-2">
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center gap-2">
              {stockCount > 0 ? (
                <button
                  type="button"
                  onClick={() => submitMove({ type: 'draw_stock' })}
                  disabled={boardInteractionDisabled}
                  className="rounded-xl bg-transparent"
                  style={{ padding: 0, border: 'none', background: 'none' }}
                >
                  <div
                    style={{ height: CARD_HEIGHT, width: CARD_WIDTH, borderRadius: 8, overflow: 'hidden', border: '1px solid #cbd5e1', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
                    className="bg-white dark:bg-slate-800"
                  >
                    <img
                      src={CARD_BACK_SRC}
                      alt="Card back"
                      style={{ height: '100%', width: '100%', objectFit: 'cover', pointerEvents: 'none' }}
                      draggable={false}
                    />
                  </div>
                </button>
              ) : (
                <EmptySlot
                  label={localPlayer?.waste?.length ? 'Reset' : 'Stock'}
                  onClick={() => submitMove({ type: 'draw_stock' })}
                  style={{ height: CARD_HEIGHT, width: CARD_WIDTH }}
                  disabled={boardInteractionDisabled}
                />
              )}
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Stock</span>
            </div>

            <div className="flex flex-col items-center gap-2">
              {wasteTop ? (
                <CardButton
                  card={wasteTop}
                  selected={selectedSource?.type === 'waste'}
                  highlighted={isHighlightedTarget('waste')}
                  onClick={() => handleAutoMove({ type: 'waste' })}
                  onDragStart={(event) => handleDragStart(event, { type: 'waste' })}
                  onDragEnd={handleDragEnd}
                  draggable
                  onTouchDragStart={(event) => handleTouchDragStart(event, { type: 'waste' })}
                  onTouchDragMove={handleTouchDragMove}
                  onTouchDragEnd={handleTouchDragEnd}
                  disabled={boardInteractionDisabled}
                  style={{height: CARD_HEIGHT, width: CARD_WIDTH}}
                  className={boardInteractionDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                />
              ) : (
                <EmptySlot label="" style={{ height: CARD_HEIGHT, width: CARD_WIDTH }} disabled={boardInteractionDisabled} />
              )}
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Waste</span>
            </div>
          </div>

          <div className="flex flex-wrap items-start gap-2">
            {foundations.map((foundation, foundationIndex) => {
              const topCard = foundation[foundation.length - 1];
              const selected = selectedSource?.type === 'foundation' && selectedSource.foundationIndex === foundationIndex;
              return (
                <div
                  key={`foundation-${foundationIndex}`}
                  className="flex flex-col items-center gap-2"
                  onDragOver={(event) => handleFoundationDragOver(event, foundationIndex)}
                  onDrop={(event) => handleFoundationDropEvent(event, foundationIndex)}
                  data-drop-target={`foundation-${foundationIndex}`}
                >
                  {topCard ? (
                    <CardButton
                      card={topCard}
                      selected={selected}
                      highlighted={isHighlightedTarget(`foundation-${foundationIndex}`)}
                      onClick={() => handleAutoMove({ type: 'foundation', foundationIndex })}
                      onDragStart={(event) => handleDragStart(event, { type: 'foundation', foundationIndex })}
                      onDragEnd={handleDragEnd}
                      draggable
                      onTouchDragStart={(event) => handleTouchDragStart(event, { type: 'foundation', foundationIndex })}
                      onTouchDragMove={handleTouchDragMove}
                      onTouchDragEnd={handleTouchDragEnd}
                      disabled={boardInteractionDisabled}
                      style={{height: CARD_HEIGHT, width: CARD_WIDTH}}
                      className={boardInteractionDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                    />
                  ) : (
                    <EmptySlot
                      label={`F${foundationIndex + 1}`}
                      foundation
                      onClick={handleFoundationDrop}
                      highlighted={isHighlightedTarget(`foundation-${foundationIndex}`)}
                      disabled={boardInteractionDisabled}
                      style={{ height: CARD_HEIGHT, width: CARD_WIDTH }}
                    />
                  )}
                  <span
                    className="inline-block rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white dark:bg-slate-100 dark:text-slate-900"
                    style={{ minWidth: 24, textAlign: 'center' }}
                  >
                    {foundationCounts[foundationIndex]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-4 pb-2">
          <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
            {tableau.map((pile, pileIndex) => {
              const height = Math.max(CARD_HEIGHT, pile.length * TABLEAU_OFFSET + CARD_HEIGHT);
              return (
                <div
                  key={`pile-${pileIndex}`}
                  className={`group flex items-start justify-center rounded-xl bg-white/35 px-0 py-1 text-left transition hover:bg-white/60 dark:bg-slate-900/30 dark:hover:bg-slate-900/60 ${isHighlightedTarget(`tableau-${pileIndex}`) ? 'ring-2 ring-sky-300 dark:ring-sky-500/50' : ''}`}
                  style={{ cursor: boardInteractionDisabled ? 'default' : 'pointer' }}
                  onDragOver={(event) => handleTableauDragOver(event, pileIndex)}
                  onDrop={(event) => handleTableauDropEvent(event, pileIndex)}
                  data-drop-target={`tableau-${pileIndex}`}
                >
                  <div className="relative" style={{ width: CARD_WIDTH, height }}>
                    {pile.length === 0 ? (
                      <EmptySlot
                        label=""
                        highlighted={isHighlightedTarget(`tableau-${pileIndex}`)}
                        disabled={boardInteractionDisabled}
                        style={{ height: CARD_HEIGHT, width: CARD_WIDTH }}
                      />
                    ) : (
                      pile.map((card, cardIndex) => {
                        const selected = selectedSource?.type === 'tableau'
                          && selectedSource.pileIndex === pileIndex
                          && cardIndex >= selectedSource.startIndex;
                        return (
                          <div
                            key={`${card.code}-${cardIndex}`}
                            className="absolute left-0"
                            style={{ top: `${cardIndex * TABLEAU_OFFSET}px` }}
                          >
                            <CardButton
                              card={card}
                              hidden={!card.faceUp}
                              selected={selected}
                              highlighted={isHighlightedTarget(`tableau-${pileIndex}`) && (!selectedSource || selectedSource.pileIndex !== pileIndex || cardIndex < selectedSource.startIndex)}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (!card.faceUp) return;
                                handleAutoMove({
                                  type: 'tableau',
                                  pileIndex,
                                  startIndex: cardIndex,
                                  topOnly: cardIndex === pile.length - 1,
                                });
                              }}
                              onDragStart={(event) => {
                                if (!card.faceUp) return;
                                handleDragStart(event, {
                                  type: 'tableau',
                                  pileIndex,
                                  startIndex: cardIndex,
                                  topOnly: cardIndex === pile.length - 1,
                                });
                              }}
                              onDragEnd={handleDragEnd}
                              draggable={card.faceUp}
                              onTouchDragStart={(event) => {
                                if (!card.faceUp) return;
                                handleTouchDragStart(event, {
                                  type: 'tableau',
                                  pileIndex,
                                  startIndex: cardIndex,
                                  topOnly: cardIndex === pile.length - 1,
                                });
                              }}
                              onTouchDragMove={handleTouchDragMove}
                              onTouchDragEnd={handleTouchDragEnd}
                              disabled={boardInteractionDisabled}
                              className={`${card.faceUp ? (boardInteractionDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer') : 'cursor-default'}`}
                              style={{ width: CARD_WIDTH, height: CARD_HEIGHT }}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      <div className="flex flex-col gap-2 rounded-2xl bg-white/80 p-3 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900/80 dark:ring-slate-800 sm:flex-row sm:items-center sm:justify-between mt-4">
        <div>
          <div className="text-sm text-slate-600 dark:text-slate-300">
            {selectionLabel}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs sm:justify-end">
          {canCompleteGame ? (
            <button
              type="button"
              onClick={handleCompleteGame}
              disabled={boardInteractionDisabled || isAutoCompleting}
              className={`rounded-full px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition ${!boardInteractionDisabled && !isAutoCompleting ? 'bg-emerald-600 text-white hover:bg-emerald-500 dark:bg-emerald-500 dark:text-slate-950 dark:hover:bg-emerald-400' : 'cursor-not-allowed bg-slate-200 text-slate-400 dark:bg-slate-800 dark:text-slate-500'}`}
            >
              {isAutoCompleting ? 'Completing...' : 'Complete game'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => submitMove({ type: 'undo' })}
            disabled={!undoCount || boardInteractionDisabled}
            className={`rounded-full px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition ${undoCount && !boardInteractionDisabled ? 'bg-slate-900 text-white hover:bg-slate-700 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white' : 'cursor-not-allowed bg-slate-200 text-slate-400 dark:bg-slate-800 dark:text-slate-500'}`}
          >
            Undo{undoCount > 1 ? ` (${undoCount})` : ''}
          </button>
          <SourceBadge selected={false}>Stock {stockCount}</SourceBadge>
          <SourceBadge selected={false}>Foundations {foundationCounts.reduce((sum, count) => sum + count, 0)}/52</SourceBadge>
          {/* Removed current player name badge as per new UX */}
        </div>

        {touchDragPreview ? (
          <div
            aria-hidden="true"
            className="pointer-events-none fixed left-0 top-0 z-50"
            style={{
              transform: `translate(${touchDragPreview.x - (CARD_WIDTH / 2)}px, ${touchDragPreview.y - 18}px)`,
            }}
          >
            <div
              style={{
                width: CARD_WIDTH,
                height: CARD_HEIGHT + Math.max(0, touchDragPreview.cards.length - 1) * TABLEAU_OFFSET,
                position: 'relative',
              }}
            >
              {touchDragPreview.cards.map((card, index) => (
                <div
                  key={`touch-preview-${card.code || `${card.rank}-${card.suit}`}-${index}`}
                  className="absolute left-0 overflow-hidden rounded-lg border border-slate-300 shadow-xl"
                  style={{
                    top: index * TABLEAU_OFFSET,
                    width: CARD_WIDTH,
                    height: CARD_HEIGHT,
                    opacity: 0.96,
                    background: '#ffffff',
                  }}
                >
                  {isMinimalCardSize ? (
                    <MinimalCard card={card} />
                  ) : (
                    <img
                      src={getCardSrc(card)}
                      alt={getCardAlt(card)}
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {allPlayers.length > 1 && (
        <div className="rounded-2xl bg-white/80 p-3 ring-1 ring-slate-200 dark:bg-slate-900/80 dark:ring-slate-800">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-sm font-semibold">Other Players</div>
            {/* Clear Selection button removed as requested */}
          </div>
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {opponentPlayers.map((player) => {
              const counts = player.foundationCounts || [0, 0, 0, 0];
              const total = counts.reduce((sum, count) => sum + count, 0);
              return (
                <div key={getPlayerId(player)} className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-200 dark:bg-slate-950/60 dark:ring-slate-800">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-medium">{player.username || 'Player'}</div>
                      {player.completionTimeDisplay ? (
                        <div className="text-xs text-emerald-600 dark:text-emerald-400">
                          Finished in {player.completionTimeDisplay}
                        </div>
                      ) : null}
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">{total}/52</div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    {counts.map((count, index) => (
                      <div key={`${getPlayerId(player)}-${index}`} className="flex min-w-0 flex-1 flex-col items-center rounded-xl bg-white px-2 py-2 text-xs ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">F{index + 1}</span>
                        <span className="mt-1 text-sm font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

SolitaireGame.Guide = function SolitaireGuide() {
  return (
    <div className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
      <p>Race through the same Klondike solitaire deal as everyone else. Every player gets an identical layout.</p>
      <p>You can either tap or drag cards to move them. Only legal moves are allowed.</p>
      <p>Use Undo to rewind your own recent moves as many times as needed before resuming play.</p>
      <p>The bottom panel shows how many cards each opponent has placed onto their four foundation piles.</p>
    </div>
  );
};

export default SolitaireGame;